'use-strict';

import PhotoSwipeLightbox from './photoswipe-lightbox.esm.min.js';
import Photoswipe from './photoswipe.esm.min.js';
// js gallery
class ZoomAction extends HTMLElement {
  constructor() {
    super();
    this.type = this.dataset.type || 'no_zoom';
    this.zoomOption = this.dataset.zoomOption || 'external';
    this.lightbox = null;
    this.lightboxBound = false;
    this.drift = null;
    this.init();
  }

  init() {
    if (this.type === 'no_zoom') return;

    // Bind once so the references are stable and removeEventListener actually works.
    this.guardClick = this.guardClick.bind(this);
    this.responsive = this.responsive.bind(this);

    // Belt-and-braces: swallow any default navigation on a gallery anchor, in the
    // capture phase. The anchors no longer carry an href (see product-media-layout
    // .liquid), so this is now redundant — but it keeps the download bug from coming
    // back if a future theme update restores the href.
    this.addEventListener('click', this.guardClick, true);

    // Keyboard support. The anchors have no href any more, so they are not real links
    // and do not fire click on Enter by themselves.
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const link = e.target.closest('a.media-gallery__image');
      if (!link) return;
      e.preventDefault();
      link.click();
    });

    // Bind the lightbox as soon as the DOM is ready. Do NOT wait for window 'load',
    // which only fires after every image, font and third-party app script has finished
    // downloading — that delay was the original bug.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.responsive, {
        once: true,
      });
    } else {
      this.responsive();
    }

    window.addEventListener('resize', this.responsive);
    window.addEventListener('load', this.responsive);
  }

  guardClick(e) {
    const link = e.target.closest('a.media-gallery__image');
    if (link) e.preventDefault();
  }

  initLightBox() {
    // Guard against re-binding. The old check (`if (this.lightbox !== null)`) never
    // worked, because this.lightbox is only assigned inside the click handler below —
    // so every resize event attached another duplicate listener to every anchor.
    if (this.lightboxBound) return;
    this.lightboxBound = true;

    this.querySelectorAll('a.media-gallery__image').forEach((a) => {
      var position = 0;
      a.addEventListener('click', (e) => {
        e.preventDefault();
        const target = e.currentTarget;
        if (target.getAttribute('data-position')) {
          position = target.getAttribute('data-position');
        } else if (
          target.closest('media-gallery').querySelector('.swiper-slide-active')
        ) {
          position = target
            .closest('media-gallery')
            .querySelector('.swiper-slide-active')
            .getAttribute('data-position');
        } else if (target.closest('media-gallery').firstChild) {
          position = target
            .closest('media-gallery')
            .firstChild?.getAttribute('data-position');
        }
        this.lightbox = new PhotoSwipeLightbox({
          gallery: this,
          children: 'a',
          pswpModule: () => Photoswipe,
          counter: false,
          zoom: false,
          preloader: false,
          arrowPrev: false,
          arrowNext: false,
          close: false,
          loop: false,
          bgOpacity: 1,
        });

        // The gallery anchors deliberately carry no href, so PhotoSwipe cannot read
        // the image URL the usual way. Feed it from data-pswp-src instead.
        this.lightbox.addFilter('domItemData', (itemData, element) => {
          const src = element.dataset.pswpSrc;
          if (src) itemData.src = src;
          return itemData;
        });

        this.lightbox.on('uiRegister', () => {
          const { pswp } = this.lightbox;
          pswp?.ui.registerElement({
            name: 'bls--close',
            isButton: true,
            tagName: 'button',
            html: '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" class="transition"><use href="#icon-close"></use></svg>',
            onClick: () => {
              pswp.close();
            },
          });
          pswp?.ui.registerElement({
            name: 'bottomBar',
            className: 'pswp__bottom-bar',
            appendTo: 'wrapper',
            onInit: (el, pswp) => {
              let next, prev, counter, useIconPrev;
              let iconNext, iconPrev, useIconNext;
              next = document.createElement('button');
              next.setAttribute('type', 'button');
              next.className = 'pswp__button pswp__button-next';
              next.innerHTML = `<svg width="6" height="11" fill="none">
              <use href="#icon-next"></use>
            </svg>`;
              next.onclick = () => {
                pswp.next();
              };
              el.appendChild(next);

              counter = document.createElement('span');
              counter.className = 'pswp__counter';
              pswp.on('change', () => {
                counter.innerText =
                  pswp.currIndex +
                  1 +
                  pswp.options.indexIndicatorSep +
                  pswp.getNumItems();
              });
              el.appendChild(counter);

              prev = document.createElement('button');
              prev.setAttribute('type', 'button');
              prev.className = 'pswp__button pswp__button-prev';
              prev.innerHTML = `<svg width="6" height="11" fill="none">
              <use href="#icon-back"></use>
            </svg>`;
              prev.onclick = () => {
                pswp.prev();
              };
              el.appendChild(prev);
            },
          });
        });
        this.lightbox.init();
        this.lightbox.loadAndOpen(position - 1);
      });
    });
  }

  initDrift() {
    if (this.drift !== null) return;
    var allTriggers = this.querySelectorAll('.drift-trigger');
    var paneContainer = this.closest('.sec__featured-product').querySelector(
      '.zoom-external-area'
    );
    if (allTriggers.length != 0) {
      allTriggers.forEach((trigger) => {
        const inlineContainer = trigger.closest('[data-pane-container]');
        const zoomOption = this.zoomOption;
        if (zoomOption === 'inner-2') {
          this.drift = new Drift(trigger, {
            inlinePane: zoomOption === 'inner-2',
            zoomFactor: 3,
            containInline: paneContainer,
            paneContainer: inlineContainer,
            hoverBoundingBox: zoomOption === 'external',
            onShow: function () {
              inlineContainer.classList.add('relative');
            },
            onHide: function () {
              inlineContainer.classList.remove('relative');
            },
          });
        } else {
          this.drift = new Drift(trigger, {
            inlinePane: zoomOption,
            zoomFactor: 5,
            containInline: !!paneContainer,
            paneContainer:
              zoomOption === 'external' ? paneContainer : inlineContainer,
            hoverBoundingBox: zoomOption === 'external',
            onShow: function () {
              inlineContainer.classList.add('relative');
            },
            onHide: function () {
              inlineContainer.classList.remove('relative');
            },
          });
        }
      });
    }
  }

  responsive() {
    if (window.innerWidth >= 768) {
      if (this.type === 'open_lightbox') {
        this.initLightBox();
      } else {
        if (this.lightbox !== null) {
          this.lightbox.destroy();
          this.lightbox = null;
        }
        this.initDrift();
      }
    } else {
      if (this.type !== 'open_lightbox') {
        if (this.drift !== null) {
          this.drift.destroy();
          this.drift == null;
        }
        this.initLightBox();
      } else {
        this.initLightBox();
      }
    }
    this.updateSwiper();
  }

  updateSwiper() {
    if (typeof Swiper !== 'undefined') {
      if (this.querySelector('.thumbnail-slide')) {
        this.querySelector('.thumbnail-slide').swiper?.update();
      }
    }
  }

  disconnectedCallback() {
    // These now actually remove the listeners. The original re-bound with .bind(this),
    // creating a brand-new function reference each time, so nothing was ever removed.
    window.removeEventListener('resize', this.responsive);
    window.removeEventListener('load', this.responsive);
    this.removeEventListener('click', this.guardClick, true);
  }
}
customElements.define('zoom-action', ZoomAction);